import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';

// @ts-ignore-next-line no types
import matrix from 'calendar-matrix';
import axios from 'axios';
import { Popover } from 'react-tiny-popover';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { debounce } from 'debounce-promise-with-cancel';

import {
  navigateMonthsLocal,
  endOfMonthLocal,
  getLocaleDateStringLocal,
} from '../../../../../../../shared/lib/time';
import { EmbedConfig } from '../../..';
import { ExportV2LimitedResult } from '../../../../../../../shared/types/exportV2';
import { DailyProgramEvent } from '../../../../../../../shared/types/dailyProgram';
import { mapBy } from '../../../../../../../shared/lib/functional';
import { PropertyZone } from '../../../../../../../shared/types/property';
import {
  getOptimizedEdpImageUrl,
  hasOptimizedEdpImageUrl,
} from '../../../../../../../shared/lib/image';
import { useApp } from '../context/AppContext';
import WideImage from '../components/WideImage';
import Icon from '../components/Icon';

const MAX_GLANCE_ITEMS_SIZE = 5;

interface CalendarPropsBase {}

type CalendarProps = CalendarPropsBase & React.HTMLProps<HTMLDivElement>;

function getDefaultGridSize(size: number) {
  return Array(size).fill(1);
}

type EmbedEvent = DailyProgramEvent & { zones: PropertyZone[] };

let popoverOutDelay: NodeJS.Timeout | undefined;
let monthDelay: NodeJS.Timeout | undefined;

function Calendar(_props: CalendarProps) {
  const { config, dynamicConstants } = useApp();
  const { isMobile } = dynamicConstants;
  const currentDateRef = useRef(new Date());
  const currentDate = currentDateRef.current;

  const [monthDate, setMonthDate] = useState(currentDate);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const dayMatrix: number[][] = useMemo(() => matrix(monthDate), [monthDate]);

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<EmbedEvent[][]>([[]]);
  const [popoverStates, setPopoverStates] = useState<{ [key: string]: boolean }>({});
  const [gridColSizes, setGridColSizes] = useState(getDefaultGridSize(7));
  const [gridRowSizes, setGridRowSizes] = useState(getDefaultGridSize(dayMatrix.length));

  const [popoverActive, setPopoverActive] = useState(false);
  const [hoverCell, setHoverCell] = useState(-99);
  const [focusCell, setFocusCell] = useState(currentDate.getDate());
  const [scrollCellReset, setScrollCellReset] = useState(-99);
  const [zones, setZones] = useState<PropertyZone[]>([]);
  const [selectedZone, setSelectedZone] = useState<string | undefined>();

  const gridDiv = useRef<HTMLDivElement>(null);
  const firstRowSet = useMemo(() => new Set(dayMatrix[0]), [dayMatrix]);
  const firstColSet = useMemo(() => new Set(dayMatrix.map(wk => wk[0])), [dayMatrix]);

  useMemo(() => {
    if (isMobile) {
      togglePopover(undefined, false);
    }
  }, [isMobile]);

  useEffect(() => {
    fetchEvents(monthDate).catch(console.error);

    return () => {
      if (popoverOutDelay) clearTimeout(popoverOutDelay);
      if (monthDelay) clearTimeout(monthDelay);

      resetState();
    };
  }, []);

  useEffect(() => {
    const states = makePopoverStates();
    setPopoverStates(states);
  }, [events]);

  useEffect(() => {
    const popoverActive = Object.values(popoverStates).includes(true);

    if (popoverOutDelay) clearTimeout(popoverOutDelay);

    if (popoverActive) {
      setPopoverActive(true);
    } else {
      const timeout = setTimeout(() => {
        setPopoverActive(false);
      }, config.animationDuration);

      popoverOutDelay = timeout;
    }
  }, [popoverStates]);

  useEffect(() => {
    resetState();
  }, [monthDate]);

  useEffect(() => {
    updateSelectedDate();
  }, [focusCell]);

  function updateGrid(d: number | undefined) {
    let x = -1;
    let y = -1;

    const newGridColSize = getDefaultGridSize(7);
    const newGridRowSize = getDefaultGridSize(dayMatrix.length);

    if (d !== undefined && d > 0) {
      dayMatrix.forEach((wk, i) => {
        if (x >= 0) return;

        x = wk.findIndex(e => d === e);

        if (x >= 0) {
          y = i;
        }
      });

      newGridColSize[x] = config.expandedRowColSize;
      newGridRowSize[y] = config.expandedRowColSize;
    }

    setGridColSizes(newGridColSize);
    setGridRowSizes(newGridRowSize);
  }

  function updateSelectedDate() {
    if (focusCell < 0) return;
    setSelectedDate(new Date(monthDate.getFullYear(), monthDate.getMonth(), focusCell));
  }

  function resetState() {
    setPopoverStates({});
    setGridColSizes(getDefaultGridSize(7));
    setGridRowSizes(getDefaultGridSize(dayMatrix.length));

    setHoverCell(-99);
    setFocusCell(
      currentDate.getMonth() === monthDate.getMonth() &&
        currentDate.getFullYear() == monthDate.getFullYear()
        ? currentDate.getDate()
        : 1
    );
    setPopoverActive(false);
    updateSelectedDate();

    if (popoverOutDelay) clearTimeout(popoverOutDelay);
  }

  function makePopoverStates() {
    const states: { [key: string]: boolean } = {};

    events.flat().forEach(e => {
      states[e.id] = false;
    });

    return states;
  }

  async function fetchEvents(date: Date) {
    setLoading(true);

    try {
      const dateIso = new Date(date).toISOString().slice(0, 8) + '01';
      const days = endOfMonthLocal(date).getDate();

      const response = await axios.get<ExportV2LimitedResult>(config.dataUrl, {
        params: { date: dateIso, days },
      });

      const zoneDict = mapBy(response.data.collections.zones, '_id');

      // LABS-4375: Events were placed in the calendar incorrectly because it was using the index of the lineup array
      // lineup array index ≠ calendar day.  lineup doesn't necessarily start from day 1 of the month
      const eventsByDay: Array<EmbedEvent[]> = Array.from({ length: days + 1 }, () => []);

      response.data.lineup.forEach(dayData => {
        const dayOfMonth = new Date(dayData.date).getUTCDate();

        eventsByDay[dayOfMonth - 1] = dayData.events.map(event => ({
          ...event,
          zones: event.zoneIds.map(zoneId => zoneDict[zoneId]),
        }));
      });
      setEvents(eventsByDay);
      setZones(response.data.collections.zones);
    } catch (error) {
      setEvents([[]]);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function changeMonth(
    direction: 1 | -1,
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    if (e) {
      e.stopPropagation();
    }

    const copyDate = navigateMonthsLocal(monthDate, direction);

    setEvents([]);
    setLoading(true);
    setMonthDate(copyDate);

    if (monthDelay) clearTimeout(monthDelay);

    const timeout = setTimeout(() => {
      fetchEvents(copyDate).catch(console.error);
    }, config.animationDuration * 2);

    monthDelay = timeout;
  }

  async function resetMonth(e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (e) {
      e.stopPropagation();
    }

    const copyDate = new Date(currentDate);

    setEvents([]);
    setLoading(true);
    setMonthDate(copyDate);

    if (monthDelay) clearTimeout(monthDelay);

    fetchEvents(copyDate).catch(console.error);
  }

  async function handleCellMouseOver(i: number, d: number) {
    if (hoverCell === d || popoverActive || loading || window.GO_IS_SCROLLING) return;

    setHoverCell(d < 0 ? -99 : d);

    if (gridDiv.current && scrollCellReset != i) {
      gridDiv.current.querySelectorAll(`.go-cell-${scrollCellReset}`).forEach(div => {
        div.children[0].scrollTo(0, 0);
      });
    }

    setScrollCellReset(i);
    updateGrid(d);
  }

  const handleCellMouseOverRef = useRef(handleCellMouseOver);
  handleCellMouseOverRef.current = handleCellMouseOver;
  const handleCellMouseOverDebounced = useRef(
    debounce(
      (i: number, d: number) => handleCellMouseOverRef.current(i, d),
      config.animationDuration * 1.5
    )
  ).current;

  function handleGridMouseOut(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    handleCellMouseOverDebounced.cancel();
    const from = e.relatedTarget as HTMLDivElement;

    if (!gridDiv || !gridDiv.current || popoverActive) return;

    if (!gridDiv.current.contains(from)) {
      setHoverCell(-99);
      updateGrid(undefined);

      gridDiv.current.querySelectorAll(`.go-cell-container`).forEach(div => {
        div.scrollTo(0, 0);
      });
    }
  }

  function handleCellClick(d: number, e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!(e.target as HTMLElement).closest('.go-event')) {
      togglePopover(undefined, false);
    }
    setHoverCell(d);
    setFocusCell(d);
    updateGrid(d);
  }

  function togglePopover(
    eId: string | undefined,
    state: boolean,
    clickEvt?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    force?: boolean
  ) {
    if (clickEvt) {
      clickEvt.stopPropagation();
    }

    const states = makePopoverStates();

    if (eId && (force || !popoverActive)) {
      states[eId] = state;
    }

    setPopoverStates(states);
  }

  function filterEvents(events: EmbedEvent[], filter: string | undefined) {
    return events.filter(e => !filter || e.zones.map(z => z._id).includes(filter));
  }

  function isExpanded(i: number) {
    return gridColSizes[i % 7] != 1;
  }

  function isActive(i: number, d: number) {
    return isExpanded(i) && hoverCell === d;
  }

  function isCurrentDay(d: number) {
    return (
      currentDate.getMonth() === monthDate.getMonth() &&
      currentDate.getFullYear() === monthDate.getFullYear() &&
      d === currentDate.getDate()
    );
  }

  function isFirstRow(d: number) {
    return firstRowSet.has(d);
  }

  function isFirstCol(d: number) {
    return firstColSet.has(d);
  }

  const gridDirty = isMobile ? false : gridColSizes.findIndex(e => e != 1) > -1;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <>
      <div
        className="border-b border-table px-4 pb-4 overflow-x-auto grid grid-cols-1 gap-4 go-heading"
        style={{ zIndex: 2 }}
      >
        <div className="flex items-center">
          {selectedDate && (
            <div
              className="transition-all whitespace-nowrap text-accent font-bold mr-2 go-current-date"
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              {getLocaleDateStringLocal(selectedDate, {
                month: 'short',
                day: 'numeric',
              })}
            </div>
          )}
          {zones.length > 0 && (
            <select
              value={selectedZone}
              onChange={e =>
                setSelectedZone(e.target.value === 'none' ? undefined : e.target.value)
              }
              className="w-full text-accent truncate px-2 py-1 mr-2 border rounded-full 
                border-accent whitespace-nowrap max-w-64 min-w-16 go-attribute-select"
            >
              <option value="none">Filter: All</option>
              {zones.map(z => (
                <option key={z._id} value={z._id} className="go-event-zone">
                  {z.name}
                </option>
              ))}
            </select>
          )}
          <div className="ml-auto whitespace-nowrap overflow-clip border rounded-full border-accent flex items-stretch go-actions">
            <button
              className="basic-button pr-1 pl-2 go-button grid go-prev-month"
              onClick={e => changeMonth(-1, e)}
            >
              <Icon icon={['fass', 'arrow-left']} className="text-accent" />
            </button>
            <button
              className="basic-button text-accent py-1 text-center w-12 go-button go-curr-month"
              onClick={resetMonth}
            >
              {getLocaleDateStringLocal(monthDate, {
                month: 'short',
              })}
            </button>
            <button
              className="basic-button pl-1 pr-2 go-button grid go-next-month"
              onClick={e => changeMonth(1, e)}
            >
              <Icon icon={['fass', 'arrow-right']} className="text-accent" />
            </button>
          </div>
        </div>
        {zones.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 go-legend">
            {zones.map(z => (
              <div className="flex items-center gap-1">
                <div
                  key={z._id}
                  className="shrink-0 bg-black rounded-full go-event-zone"
                  style={{ backgroundColor: z.color, width: '10px', height: '10px' }}
                ></div>
                <div className="text-sm text-accent go-event-zone-name">{z.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div
        className={`bg-white py-2 grid transition-all${
          loading ? ' opacity-50' : ''
        } go-day-headings`}
        style={{
          gridTemplateColumns: (isMobile ? getDefaultGridSize(7) : gridColSizes)
            .map(x => `minmax(0,${x}fr)`)
            .join(' '),
        }}
      >
        {days.map((d, i) => (
          <div
            key={d}
            className={`text-center text-secondary${
              !isMobile && isExpanded(i) ? ' text-accent' : ''
            } go-day-name go-day-name-${i}`}
          >
            {d}
          </div>
        ))}
      </div>
      <div style={{ paddingBottom: `${(dayMatrix.length / 7) * 100}%`, position: 'relative' }}>
        <div
          ref={gridDiv}
          className={`bg-white grid transition-all${loading ? ' opacity-50' : ''} go-grid`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            gridTemplateColumns: (isMobile ? getDefaultGridSize(7) : gridColSizes)
              .map(x => `minmax(0,${x}fr)`)
              .join(' '),
            gridTemplateRows: (isMobile ? getDefaultGridSize(dayMatrix.length) : gridRowSizes)
              .map(x => `minmax(0,${x}fr)`)
              .join(' '),
          }}
          onMouseOut={handleGridMouseOut}
        >
          {dayMatrix.flat().map((d, i) => (
            <div
              key={d}
              className={`relative w-full transition-all${
                isFirstRow(d)
                  ? ''
                  : isCurrentDay(d) || d === focusCell || d === hoverCell
                    ? ' border-t-2'
                    : ' border-t'
              }${
                isFirstCol(d)
                  ? ''
                  : isCurrentDay(d) || d === focusCell || d === hoverCell
                    ? ' border-l-2'
                    : ' border-l'
              }${
                d === focusCell
                  ? ' border-active'
                  : d === hoverCell
                    ? ' border-accent'
                    : isCurrentDay(d)
                      ? ' border-emphasis'
                      : ' border-table'
              }${gridDirty ? (isActive(i, d) ? '' : ' opacity-50') : ''} go-cell go-cell-${i}${
                isCurrentDay(d) ? ' active' : ''
              }`}
            >
              <div
                className="fullscreen go-cell-container"
                style={{
                  opacity: isActive(i, d) ? 1 : !gridDirty ? 1 : 0.5,
                  overflowY: isActive(i, d) ? 'auto' : !gridDirty ? 'auto' : 'hidden',
                }}
                onMouseOver={() => handleCellMouseOverDebounced(i, d)}
                onClick={e => handleCellClick(d, e)}
              >
                {isMobile && filterEvents(events[d - 1] || [], selectedZone).length > 0 && (
                  <div
                    className="bg-emphasis go-cell-marker"
                    style={{
                      position: 'absolute',
                      bottom: 5,
                      right: 5,
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                    }}
                  ></div>
                )}
                <div
                  className={`pl-2 pt-1 pr-0 font-bold${d < 0 ? ' hidden' : ''}${
                    focusCell === d
                      ? ' text-active'
                      : hoverCell === d
                        ? ' text-accent'
                        : isCurrentDay(d)
                          ? ' text-emphasis'
                          : d < 0
                            ? ' text-disabled'
                            : ' text-secondary'
                  } go-day`}
                >
                  {Math.abs(d)}
                </div>
                <div className={`${d < 1 ? 'hidden ' : ''}go-events`}>
                  {!isMobile &&
                    filterEvents(events[d - 1] || [], selectedZone)
                      .slice(0, isActive(i, d) ? undefined : MAX_GLANCE_ITEMS_SIZE)
                      .map(evt => (
                        // @ts-ignore
                        <Popover
                          key={evt.id}
                          boundaryElement={gridDiv.current || undefined}
                          parentElement={gridDiv.current || undefined}
                          isOpen={popoverStates[evt.id]}
                          positions={['right', 'left', 'top', 'bottom']} // preferred positions by priority
                          containerStyle={{ zIndex: '3' }}
                          content={
                            <PopoverContent
                              event={evt}
                              config={config}
                              togglePopover={togglePopover}
                              className={`w-80 border border-border shadow-lg go-event-popover go-event-popover-${i}`}
                            />
                          }
                        >
                          <button
                            className={`basic-button min-w-full text-left py-1 pl-2 pr-0 flex${
                              popoverStates[evt.id] ? ' bg-highlight' : ''
                            } content-stretch go-event go-event-${i}`}
                            onClick={() =>
                              togglePopover(
                                evt.id,
                                !popoverStates[evt.id],
                                undefined,
                                isActive(i, d)
                              )
                            }
                          >
                            {hasOptimizedEdpImageUrl(
                              evt.imageOptimizations,
                              config.imgRequirements,
                              isMobile ? 1 : 0
                            ) && (
                              <div
                                className={`transition-all${
                                  isActive(i, d) ? ' mr-2 go-event-img-width' : ' go-event-img-hide'
                                } flex-none go-event-img-wrapper`}
                              >
                                <div className="rectangle relative border-1 overflow-clip go-event-img-container">
                                  <WideImage
                                    title={`Image of ${evt.title}`}
                                    className="rounded-0-i"
                                    image={getOptimizedEdpImageUrl(
                                      evt.images,
                                      evt.imageOptimizations,
                                      config.imgRequirements,
                                      isMobile ? 1 : 0
                                    )}
                                  />
                                </div>
                              </div>
                            )}

                            <div className="w-full flex-grow overflow-x-hidden go-event-container">
                              <div className="text-accent text-sm font-bold truncate flex items-center go-event-name">
                                <div
                                  className={`${isActive(i, d) ? '' : 'flex gap-1 items-center'}`}
                                >
                                  <div className="flex gap-1 items-center go-event-zones">
                                    {evt.zones.map(z => (
                                      <Fragment key={z._id}>
                                        {isActive(i, d) ? (
                                          <div
                                            className="text-white shrink-0 min-w-0 bg-black font-bold rounded-full px-1 text-xs truncate go-event-zone"
                                            style={{ backgroundColor: z.color }}
                                          >
                                            {z.name}
                                          </div>
                                        ) : (
                                          <div
                                            className="shrink-0 bg-black rounded-full go-event-zone"
                                            style={{
                                              backgroundColor: z.color,
                                              width: '10px',
                                              height: '10px',
                                            }}
                                          ></div>
                                        )}
                                      </Fragment>
                                    ))}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {evt.isTrending && (
                                      <Icon
                                        icon={['fass', 'fire']}
                                        dynamicWidth
                                        className="text-trending"
                                      />
                                    )}
                                    {evt.isPriced && (
                                      <Icon
                                        icon={['fass', 'dollar-circle']}
                                        dynamicWidth
                                        className="text-priced"
                                      />
                                    )}
                                    {evt.title}
                                  </div>
                                </div>
                              </div>
                              <div className="text-sm truncate go-event-time">
                                {/* FIXME: Why is this an array sometimes? */}
                                {evt.timeString.toString()}
                              </div>
                            </div>
                          </button>
                        </Popover>
                      ))}
                  {!isMobile &&
                    !isActive(i, d) &&
                    (events[d - 1] || []).length > MAX_GLANCE_ITEMS_SIZE && (
                      <div className="py-1 pl-2 pr-0 flex content-stretch go-event">& More</div>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {isMobile && (
        <div className="grid gap-3 go-mobile-events pt-3 border-t border-b border-border">
          <div className="text-xl text-accent font-bold px-4">Lineup</div>
          {filterEvents(events[focusCell - 1] || [], selectedZone).map((evt, i) => (
            <PopoverContent
              key={evt.id}
              event={evt}
              config={config}
              togglePopover={togglePopover}
              noClose
              className={`w-full border-t border-b border-border go-event-popover go-event-popover-${i}`}
            />
          ))}
          {filterEvents(events[focusCell - 1] || [], selectedZone).length < 1 && (
            <div className="px-3 pb-6 text-muted text-center">
              {loading ? 'Loading...' : 'No content available'}
            </div>
          )}
        </div>
      )}
    </>
  );
}

interface PopoverContentPropsBase {
  event: EmbedEvent;
  config: EmbedConfig;
  imgClassName?: string;
  noClose?: boolean;
  togglePopover: (
    eId: string | undefined,
    state: boolean,
    clickEvt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
}

type PopoverContentProps = PopoverContentPropsBase & React.HTMLProps<HTMLDivElement>;

export function PopoverContent(props: PopoverContentProps) {
  const { event, config, className, togglePopover, noClose, imgClassName, ...rest } = props;

  const classes = ['relative bg-white', className];
  const imgClasses = ['rounded-0-i'];

  if (imgClassName) imgClasses.push(imgClassName);
  imgClasses.push('go-ex-evt-img-wrapper');

  const hasImage = event.images.length > 0;

  return (
    <div className={classes.join(' ')} {...rest}>
      {!noClose && (
        <button
          className="basic-button bg-accent go-event-close"
          onClick={e => togglePopover(event.id, false, e)}
        >
          <Icon icon={['fass', 'xmark']} className="text-white" />
        </button>
      )}
      {hasImage && (
        <WideImage
          title={`Image of ${event.title}`}
          className={imgClasses.join(' ')}
          image={getOptimizedEdpImageUrl(
            event.images,
            event.imageOptimizations,
            config.imgRequirements,
            0
          )}
        />
      )}
      <div className="p-4 w-full flex-grow grid grid-cols-1 gap-2 go-ex-event-container">
        <div>
          <div className="flex flex-wrap gap-1 mb-1">
            {event.zones.map(z => (
              <div
                key={z._id}
                className="text-white shrink-0 min-w-0 bg-black font-bold rounded-full px-1 text-xs truncate go-event-zone"
                style={{ backgroundColor: z.color }}
              >
                {z.name}
              </div>
            ))}
          </div>
          <div className="text-accent font-bold text-lg go-ex-event-name">{event.title}</div>
          <div className="text-sm italic go-ex-event-venue">{event.venueString}</div>
          <div className="text-sm go-ex-event-time">
            {/* FIXME: Why is this an array sometimes? */}
            {event.timeString.toString()}
          </div>
        </div>
        {event.description && (
          <div className="text-sm go-ex-event-description">{event.description}</div>
        )}
        {event.callToActions.length > 0 && (
          <div>
            {event.callToActions.map(cta => (
              <div className="relative" key={cta.label}>
                <a
                  className="font-bold text-active stretched-link go-ex-event-cta"
                  href={cta.link}
                  target={cta.target}
                >
                  {cta.label}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Calendar;
